import React, { useMemo, useRef, useState } from 'react';
import {
  Button,
  InfoGroup,
  InfoItem,
  InfoItemKey,
  InfoItemValue,
  Box,
  Text,
  Link,
  Heading,
  CheckCircleIcon,
  EditIcon,
  StepItemIcon,
  Spinner,
  useToast,
  Badge,
  AlertOnlyIcon,
} from '@razorpay/blade/components';
import { useHistory } from 'react-router-dom';
import { useMutation } from 'react-query';
import api from 'api';
import { useFormWithProvider } from 'forms';
import { routePaths } from 'components/Routes/data';
import { FormFields } from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/types';
import { OnboardingCompleted } from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/components/OnboardingCompleted';
import { useGetBankAccountDetails } from 'components/EmployeeDetails/BankAccountDetails/hooks/useGetBankAccountDetails';
import { parseGraphqlErrorFromExtensions } from 'utils/Api';
import { AppError } from 'utils/AppError';
import { dateFormats, getFormattedDateValue } from 'utils/Dates';
import { getGenderTitle, getStateName, reviewDetailsValidationSchema } from './utils';
import { useSavedEmployeeData } from './hooks/useSavedEmployeeData';
import { useOptionalFields } from './hooks/useOptionalFields';
import { useAdditionalDetailsStep } from './hooks/useAdditionalDetailsStep';
import { updateMyCustomFields } from 'api/gql-utils/employeeCustomFields/mutations/updateMyCustomFields';
import { formatFieldValue } from 'components/EmployeeCustomFields/utils';
import {
  encodeFieldValuesForSubmit,
  filterToEditableValues,
  validateCustomFieldValues,
} from 'components/EmployeeCustomFields/validation';
import { CustomFieldValueMap } from 'components/EmployeeCustomFields/types';
import { getClientSafeMessage } from 'components/EmployeeCustomFields/errors';
import {
  FormHeading,
  StyledForm,
  FormFooterWrapper,
  FormFieldsWrapper,
  BorderedBox,
} from 'components/EmployeeOnboarding/components/styles';
import { onboardingErrorMessages } from '../utils';
import useFeature from 'hooks/useFeature';
import { useOfficeLocationById } from '../CreateEmployee/hooks/useOfficeLocationById';

interface DetailItem {
  label: string;
  value?: string | null;
  shouldShowRequiredBadge?: boolean;
  shouldHide?: boolean;
}

interface Section {
  title: string;
  route: string;
  items: DetailItem[];
  canEdit?: boolean;
  shouldHide?: boolean;
}

export const ReviewDetailsInfoValue = ({ detailItem }: { detailItem: DetailItem }) => {
  if (detailItem.shouldHide) {
    return null;
  }

  if (detailItem.shouldShowRequiredBadge) {
    return (
      <InfoItem key={detailItem.label}>
        <InfoItemKey leading={() => <StepItemIcon icon={AlertOnlyIcon} color="notice" />}>
          {detailItem.label}
        </InfoItemKey>
        <InfoItemValue>
          <Badge color="negative">Required</Badge>
        </InfoItemValue>
      </InfoItem>
    );
  }

  return (
    <InfoItem key={detailItem.label}>
      <InfoItemKey leading={() => <StepItemIcon icon={CheckCircleIcon} color="positive" />}>
        {detailItem.label}
      </InfoItemKey>
      <InfoItemValue>
        <Text weight="regular">{detailItem.value || '-'}</Text>
      </InfoItemValue>
    </InfoItem>
  );
};

export const ReviewDetails = (): React.ReactElement => {
  const [showSocialMediaModal, setShowSocialMediaModal] = useState(false);
  // Guards double-submit across the whole chained submit (selfOnboarding + updateMyCustomFields);
  // mutateEmployeeSelfOnboarding.isLoading alone leaves a gap during the custom fields leg.
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Tracks that the non-HRIS info update (selfOnboarding mutation) already
  // succeeded, so a retry re-runs only the HRIS custom-fields leg — it does not
  // re-POST the core onboarding info (which would re-fire audit/dedup).
  const nonHrisInfoUpdateSucceededRef = useRef(false);
  const history = useHistory();
  const toast = useToast();
  const { formData, validate, errors, setFieldValue } = useFormWithProvider<FormFields>();
  const {
    isLoadingQuery,
    employeeDetails: { basicDetails, complianceDetails },
    shouldShowStatutoryDetailsStep,
  } = useSavedEmployeeData();

  const { shouldShowAdditionalDetailsStep, editableDefinitions } = useAdditionalDetailsStep();

  const {
    latestBankAccount,
    queryResponse: { isLoading, isFetching },
  } = useGetBankAccountDetails();

  const { isEsicMultiLocationEnabled } = useFeature('esicMultiLocation');

  const { data: selectedOfficeLocation, isFetchingOfficeLocation } = useOfficeLocationById(
    basicDetails?.officeLocationId,
  );

  const { optionalFieldsKeys, optionalFields } = useOptionalFields();

  const customFields = formData.customFields ?? {};

  // Step 5 (Additional Details) is the only place mandatory custom fields are
  // enforced. Deep-linking straight to /review-details bypasses that gate, so we
  // ALSO re-check every REQUIRED, employee-EDITable custom field here (reusing
  // the same step-5 validation util) — otherwise final submit could proceed with
  // required custom fields empty.
  const customFieldViolations = useMemo(
    () =>
      shouldShowAdditionalDetailsStep
        ? validateCustomFieldValues(editableDefinitions, customFields as CustomFieldValueMap)
        : [],
    [shouldShowAdditionalDetailsStep, editableDefinitions, customFields],
  );
  const areCustomFieldsValid = customFieldViolations.length === 0;

  const isFormValid = useMemo(
    () => validate(reviewDetailsValidationSchema) && areCustomFieldsValid,
    [validate, areCustomFieldsValid],
  );

  const sections: Section[] = [
    {
      title: 'Basic Details',
      route: routePaths.employeeOnboarding.employeeSelfOnboarding.personalDetails,
      canEdit: false,
      items: [
        { label: 'Name', value: basicDetails?.name },
        { label: 'Email ID', value: basicDetails?.email },
        { label: 'Joining Date', value: basicDetails?.hireDate },
        { label: 'Designation', value: basicDetails?.designation },
        { label: 'Department', value: basicDetails?.department },
        { label: 'Manager', value: basicDetails?.managerName },
        {
          label: 'Location',
          value: isEsicMultiLocationEnabled
            ? selectedOfficeLocation?.office_name
            : getStateName(basicDetails?.location),
        },
      ],
    },
    {
      title: 'KYC Information',
      route: routePaths.employeeOnboarding.employeeSelfOnboarding.kycDetails,
      canEdit: true,
      items: [
        { label: 'PAN', value: formData.pan, shouldShowRequiredBadge: !!errors.pan },
        {
          label: 'Aadhar Number',
          value: formData.aadharNumber,
          shouldShowRequiredBadge: !!errors.aadharNumber,
        },
      ],
    },
    {
      title: 'Bank Account Details',
      route: routePaths.employeeOnboarding.employeeSelfOnboarding.bankDetails,
      canEdit: true,
      items: [
        {
          label: 'Bank Account Number',
          value: latestBankAccount?.account_number,
          shouldShowRequiredBadge: !latestBankAccount?.account_number,
        },
        { label: 'Bank IFSC Code', value: latestBankAccount?.ifsc },
        { label: 'Beneficiary', value: latestBankAccount?.beneficiary_name },
      ],
    },
    {
      title: 'Statutory Information',
      route: routePaths.employeeOnboarding.employeeSelfOnboarding.statutoryDetails,
      canEdit: true,
      items: [
        {
          label: 'PF UAN',
          value: formData.doNotHavePfUan ? 'Not Available' : formData.pfUan,
          shouldShowRequiredBadge: !!errors.pfUan,
          shouldHide: !complianceDetails.isPfEnabled,
        },
        {
          label: 'ESIC IP Number',
          value: formData.doNotHaveEsicIpNumber ? 'Not Available' : formData.esicIpNumber,
          shouldShowRequiredBadge: !!errors.esicIpNumber,
          shouldHide: !complianceDetails.isEsicEnabled,
        },
      ],
      shouldHide: !shouldShowStatutoryDetailsStep,
    },
    {
      title: 'Personal Details',
      route: routePaths.employeeOnboarding.employeeSelfOnboarding.personalDetails,
      canEdit: true,
      items: [
        {
          label: 'Phone Number',
          value: formData.phoneNumber,
          shouldShowRequiredBadge: !!errors.phoneNumber,
        },
        {
          label: 'WhatsApp Integration',
          value: formData.enableWhatsapp ? 'Enabled' : 'Disabled',
        },
        { label: 'Gender', value: getGenderTitle(formData.gender?.[0]) },
        {
          label: 'Date of Birth',
          value: getFormattedDateValue({
            date: formData.dob,
            formatString: dateFormats.humanDateMonthYear,
            filler: '-',
          }),
          shouldShowRequiredBadge: !!errors.dob,
        },
        ...optionalFieldsKeys
          .filter((key) => !!formData[key])
          .map((key) => ({
            label: optionalFields[key].label,
            value:
              key === 'marriageAnniversary'
                ? getFormattedDateValue({
                    date: formData[key],
                    formatString: dateFormats.humanDateMonthYear,
                    filler: '-',
                  })
                : key === 'maritalStatus'
                ? formData[key]?.[0]
                : String(formData[key]),
          })),
      ],
    },
    {
      title: 'Additional Details',
      route: routePaths.employeeOnboarding.employeeSelfOnboarding.additionalDetails,
      canEdit: true,
      items: editableDefinitions.map((def) => ({
        label: def.label ?? '',
        value: formatFieldValue(def, customFields[def.fieldKey ?? ''] ?? null),
        // Surface a Required badge for empty mandatory custom fields (mirrors the
        // other sections) so the user knows exactly what to fix on the step.
        shouldShowRequiredBadge: customFieldViolations.some(
          (violation) => violation.field === (def.fieldKey ?? ''),
        ),
      })),
      shouldHide: !shouldShowAdditionalDetailsStep,
    },
  ];

  const mutateEmployeeSelfOnboarding = useMutation({
    mutationFn: api.employeeOnboardingGql.mutations.selfOnboarding,
    onError: (error: typeof AppError) => {
      const errorMessage = parseGraphqlErrorFromExtensions(error);
      if (errorMessage === onboardingErrorMessages.panExists) {
        setFieldValue('panVerificationStatus', false);
      }
      toast.show({
        content: errorMessage,
        color: 'negative',
      });
    },
  });

  const handleBack = () => {
    history.push(
      shouldShowAdditionalDetailsStep
        ? routePaths.employeeOnboarding.employeeSelfOnboarding.additionalDetails
        : routePaths.employeeOnboarding.employeeSelfOnboarding.personalDetails,
    );
  };

  const handleContinue = async () => {
    // Backstop for the step-5 mandatory gate: if a required, editable custom
    // field is empty (e.g. the user deep-linked past the Additional Details
    // step) do NOT submit — guide them back to fill it in.
    if (!areCustomFieldsValid) {
      toast.show({
        content: 'Please complete the required fields in Additional Details.',
        color: 'negative',
      });
      history.push(routePaths.employeeOnboarding.employeeSelfOnboarding.additionalDetails);
      return;
    }
    setIsSubmitting(true);
    // Submit is a two-leg chain: (1) the selfOnboarding GraphQL mutation that
    // saves the non-HRIS / core onboarding info, then (2) updateMyCustomFields
    // against the HRIS service. If leg 2 fails, the user stays on this screen and
    // presses Continue again — that retry must NOT re-run leg 1 (it already
    // succeeded and is not idempotent, e.g. PAN-exists checks), so we skip it via
    // this ref and re-run only the failed custom-fields leg.
    if (!nonHrisInfoUpdateSucceededRef.current) {
      try {
        await mutateEmployeeSelfOnboarding.mutateAsync({
          pan: formData.pan,
          aadhaar: formData.aadharNumber,
          pfUan: formData.pfUan,
          esicIp: formData.esicIpNumber,
          phoneNumber: formData.phoneNumber,
          gender: formData.gender?.[0],
          dob: getFormattedDateValue({
            date: formData.dob,
            formatString: dateFormats.yearMonthDate,
            filler: '',
          }),
          enableWhatsapp: formData.enableWhatsapp ?? false,
          optionalEmployeeData: {
            ...optionalFieldsKeys.reduce((acc, key) => {
              if (key === 'marriageAnniversary') {
                acc[key] = getFormattedDateValue({
                  date: formData[key],
                  formatString: dateFormats.yearMonthDate,
                  filler: '',
                });
              } else if (key === 'maritalStatus') {
                acc[key] = formData[key]?.[0] || '';
              } else {
                acc[key] = formData[key] || '';
              }
              return acc;
            }, {} as Record<string, string>),
          },
        });
        nonHrisInfoUpdateSucceededRef.current = true;
      } catch {
        // onError handler on the mutation already shows the toast; bail out
        setIsSubmitting(false);
        return;
      }
    }

    // Chain: update custom fields if the Additional Details step is shown and there are values
    if (shouldShowAdditionalDetailsStep && Object.keys(customFields).length > 0) {
      try {
        // MONEY values must be { amount_paise } objects on the wire.
        await updateMyCustomFields({
          // EDIT-only payload: drop any VIEW-only values that leaked into form state.
          fields: encodeFieldValuesForSubmit(
            editableDefinitions,
            filterToEditableValues(editableDefinitions, customFields),
          ),
          mode: 'MERGE',
        });
      } catch (error) {
        // Surface the server's client-safe message (e.g. FIELD_VALIDATION_FAILED /
        // FORBIDDEN / CONFLICT / NOT_FOUND) so the user knows which field/why,
        // falling back to the generic copy for opaque/transient failures.
        const serverMessage = getClientSafeMessage(error);
        toast.show({
          content: serverMessage || 'Additional details could not be saved. Please retry.',
          color: 'negative',
        });
        // Do not navigate — the user should retry. Because
        // nonHrisInfoUpdateSucceededRef is now set, the retry re-runs ONLY the
        // custom-fields leg (no re-run of the non-HRIS info update).
        setIsSubmitting(false);
        return;
      }
    }

    setIsSubmitting(false);
    setShowSocialMediaModal(true);
  };

  const handleEditClick = (route: string) => {
    history.push(route);
  };

  const isLoadingDataQueries =
    isLoadingQuery || isLoading || isFetching || isFetchingOfficeLocation;

  return (
    <>
      <StyledForm>
        <FormFieldsWrapper>
          <FormHeading
            title="Review Details"
            description="Review all information carefully before finalising your profile."
          />

          <Box display="flex" flexDirection="column" gap="spacing.7">
            {isLoadingDataQueries ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Spinner accessibilityLabel="loading" />
              </Box>
            ) : (
              sections.filter((section) => !section.shouldHide).map((section) => (
                <BorderedBox key={section.title}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Heading size="medium" color="surface.text.gray.normal" weight="medium">
                      {section.title}
                    </Heading>
                    {section.canEdit && (
                      <Link
                        variant="button"
                        icon={EditIcon}
                        iconPosition="left"
                        onClick={() => handleEditClick(section.route)}>
                        Edit
                      </Link>
                    )}
                  </Box>

                  <InfoGroup
                    itemOrientation="horizontal"
                    size="medium"
                    valueAlign="right"
                    maxWidth="100%">
                    {section.items.map((item) => (
                      <ReviewDetailsInfoValue key={item.label} detailItem={item} />
                    ))}
                  </InfoGroup>
                </BorderedBox>
              ))
            )}
          </Box>
        </FormFieldsWrapper>

        <FormFooterWrapper>
          <Button
            variant="tertiary"
            size="medium"
            onClick={handleBack}
            isDisabled={mutateEmployeeSelfOnboarding.isLoading || isSubmitting}>
            Back
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={handleContinue}
            isDisabled={isLoadingDataQueries || !isFormValid || isSubmitting}
            isLoading={isSubmitting}>
            Continue
          </Button>
        </FormFooterWrapper>
      </StyledForm>

      <OnboardingCompleted
        isOpen={showSocialMediaModal}
        onDismiss={() => setShowSocialMediaModal(false)}
        designation={basicDetails?.designation}
      />
    </>
  );
};
